import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { Search, Users } from "lucide-react";
import { fmtDate } from "../lib/format.js";
import PageHeader from "../components/PageHeader.jsx";

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.append("q", q.trim());
      const { data } = await api.get(`/customers?${params}`);
      setCustomers(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [q]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        overline="CRM"
        title="Clientes"
        subtitle={loading ? "A carregar…" : `${customers.length} cliente${customers.length === 1 ? "" : "s"} na base de dados`}
        actions={
          <button
            onClick={() => navigate("/vehicle-registrations")}
            className="text-sm border border-ronil-navy text-ronil-navy hover:bg-ronil-navy hover:text-white rounded-md px-4 py-2 font-heading font-semibold transition-colors"
            data-testid="customers-goto-vr-btn"
          >
            Registo de viaturas
          </button>
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 shadow-soft-card">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold">Pesquisar</span>
          <div className="relative mt-1.5">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome, telefone, email, NUI…"
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30"
              data-testid="customers-search-input"
            />
            <Search className="w-4 h-4 text-[#6C757D] absolute left-2 top-1/2 -translate-y-1/2" />
          </div>
        </label>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
            <tr>
              <Th>Nº</Th><Th>Nome</Th><Th>Telefone</Th><Th>Email</Th>
              <Th>Viaturas</Th><Th>Última compra</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#6C757D]">A carregar…</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-[#6C757D]" data-testid="customers-empty">Sem clientes registados</td></tr>
            ) : customers.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/crm/customers/${c.id}`)}
                data-testid={`customer-row-${c.id}`}
                className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer"
              >
                <Td mono>{c.customer_number}</Td>
                <Td className="font-medium">{c.name}</Td>
                <Td>{c.phone}</Td>
                <Td className="text-[#6C757D]">{c.email || "—"}</Td>
                <Td>
                  <span className="inline-flex items-center gap-1 bg-[#F4F6F8] rounded-sm px-2 py-0.5 text-xs">
                    <span className="font-semibold">{c.vehicle_count}</span> viatura{c.vehicle_count === 1 ? "" : "s"}
                  </span>
                </Td>
                <Td className="text-[#6C757D]">{c.last_purchase_date ? fmtDate(c.last_purchase_date) : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }
